import * as React from "react";
import { useMemo, useState } from "react";
import {
  ReferenceField,
  ReferenceArrayField,
  WithListContext,
  TextField,
  Link,
  DataTable,
  FunctionField,
  useRecordContext,
  Title,
  useGetList,
  Loading,
  useGetRecordId,
  ListContextProvider,
  DateField,
  Pagination,
  SimpleShowLayout,
  Labeled,
  usePermissions,
} from "react-admin";
import { Navigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { ROLE_TYPES } from "../enums/Roles";

const PER_PAGE = 10;
const DEFAULT_SORT = { field: "createdAt", order: "DESC" };

const AuditTitle = () => <Title title="Historial" />;

const DIFF_SIDES = ["oldValue", "newValue"];

/**
 * The audited field names match the resource names, so one map covers every
 * audited entity — including the ones that do not have an audit page yet.
 */
const REFERENCE_BY_FIELD = {
  products: "products",
  restaurants: "restaurants",
  videos: "videos",
  regions: "regions",
  categories: "categories",
};

/** An id that no longer resolves stays visible: the history must not shrink. */
const DiffEntity = ({ id, reference, record }) => {
  if (!record) return <li>{id}</li>;

  return (
    <li>
      <Link to={`/${reference}/${id}/show`}>{record.name}</Link>
      {record.restaurant && (
        <Typography
          component="span"
          variant="caption"
          color="text.secondary"
          sx={{ ml: 1 }}
        >
          <ReferenceField
            record={record}
            source="restaurant"
            reference="restaurants"
            link={false}
          >
            <TextField source="name" />
          </ReferenceField>
        </Typography>
      )}
    </li>
  );
};

const DiffValue = ({ auditRecord, field, side }) => {
  const value = auditRecord.diffChanges[field][side];
  const reference = REFERENCE_BY_FIELD[field];

  if (!reference || !Array.isArray(value)) {
    return <pre>{JSON.stringify(value, null, 2)}</pre>;
  }

  if (!value.length) return <pre>[]</pre>;

  return (
    <ReferenceArrayField
      record={auditRecord}
      source={`diffChanges.${field}.${side}`}
      reference={reference}
    >
      <WithListContext
        render={({ data }) => (
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {value.map((id) => (
              <DiffEntity
                key={id}
                id={id}
                reference={reference}
                record={(data ?? []).find((entity) => entity.id === id)}
              />
            ))}
          </Box>
        )}
      />
    </ReferenceArrayField>
  );
};

const AuditPanel = () => {
  const record = useRecordContext();
  // The API already sends only the fields that changed.
  const diffChanges = record?.diffChanges ?? {};

  return (
    <SimpleShowLayout>
      {Object.keys(diffChanges).map((key) => {
        if (key === "updatedAt") return null;
        return (
          <Box key={key} sx={{ flex: 1 }}>
            {DIFF_SIDES.map((side) => (
              <Box key={side} sx={{ flex: 0.5 }}>
                <Labeled label={key}>
                  <FunctionField
                    source={`diffChanges.${key}.${side}`}
                    label={key}
                    render={(auditRecord) => (
                      <DiffValue
                        auditRecord={auditRecord}
                        field={key}
                        side={side}
                      />
                    )}
                  />
                </Labeled>
              </Box>
            ))}
          </Box>
        );
      })}
    </SimpleShowLayout>
  );
};

/**
 * The audit trail of one record, as a standalone page.
 *
 * `resource` is the API path of the entity ("users", "banners"). The nested
 * audits path is passed to useGetList as the resource name, which is what the
 * data provider interpolates into the URL.
 */
export const AuditList = ({ resource }) => {
  const recordId = useGetRecordId();
  const [page, setPage] = useState(1);
  const { permissions, isPending: isLoadingPermissions } = usePermissions();
  const auditsResource = `${resource}/${recordId}/audits`;

  const isAdmin = permissions === ROLE_TYPES.ADMIN;

  // Held back until the gate below resolves: a nested custom route is outside
  // react-admin's resource access control, so a non-admin reaching this URL
  // would otherwise fire a request the API only rejects afterwards.
  const { data, total, isPending } = useGetList(
    auditsResource,
    {
      pagination: { page, perPage: PER_PAGE },
      sort: DEFAULT_SORT,
    },
    { enabled: !isLoadingPermissions && isAdmin }
  );

  const listContext = useMemo(
    () => ({
      data: data || [],
      total: total || 0,
      page,
      perPage: PER_PAGE,
      setPage,
      sort: DEFAULT_SORT,
    }),
    [data, total, page]
  );

  if (isLoadingPermissions) return <Loading />;

  // The route is reachable by URL, so the gate lives here rather than per page.
  if (!isAdmin) return <Navigate to="/" />;

  // A disabled query never leaves the pending state, so this comes after the gate.
  if (isPending) return <Loading />;

  return (
    <ListContextProvider value={listContext}>
      <AuditTitle />
      <DataTable
        resource={auditsResource}
        bulkActionButtons={false}
        rowClick="expand"
        expand={<AuditPanel />}
        expandSingle
      >
        <DataTable.Col source="user.id" label="Modificado por">
          <ReferenceField
            label="Modificado por"
            reference="users"
            source="user.id"
            link="show"
          >
            <FunctionField
              source="firstName"
              render={(record) =>
                `${record?.firstName || ""} ${record?.lastName || ""}` || "-"
              }
            />
          </ReferenceField>
        </DataTable.Col>
        <DataTable.Col source="createdAt" label="Fecha">
          <DateField
            source="createdAt"
            label="Fecha"
            showTime={true}
            emptyText="-"
          />
        </DataTable.Col>
      </DataTable>
      <Pagination rowsPerPageOptions={[PER_PAGE]} />
    </ListContextProvider>
  );
};

export default AuditList;
